import { AuthPanel } from './auth-panel'
import { AuthAgreementCheckbox } from './auth-agreement-checkbox'
import { Button, InputField, PasswordInput } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'

export type AuthPanelPasswordVariant = 'desktop' | 'mobile'

export type AuthPanelPasswordProps = {
  variant?: AuthPanelPasswordVariant
  title?: string
  emailLabel?: string
  emailPlaceholder?: string
  emailValue?: string
  onEmailChange?: (value: string) => void
  passwordLabel?: string
  passwordPlaceholder?: string
  passwordValue?: string
  onPasswordChange?: (value: string) => void
  passwordHelperText?: string
  showAgreementCheckbox?: boolean
  agreementChecked?: boolean
  onAgreementChange?: (checked: boolean) => void
  agreementErrorText?: string
  legalLinksReturnTo?: string
  submitLabel?: string
  onSubmit?: () => void
  submitDisabled?: boolean
  actionButtonsDisabled?: boolean
  onForgotPassword?: () => void
  onMagicLink?: () => void
  registrationHref?: string
  footerMessageText?: string
  footerMessageTone?: 'default' | 'success' | 'error'
  className?: string
}

const authFooterTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: designTokens.typography.body.base.size,
  lineHeight: 'normal',
  fontWeight: designTokens.typography.body.base.weight,
}

const authFooterActionStyle = {
  ...authFooterTextStyle,
  ['--auth-footer-action-color' as string]: designTokens.colors.text.primary,
  ['--auth-footer-action-hover' as string]: designTokens.colors.brand.primary,
}

const authFooterActionClassName =
  'text-[var(--auth-footer-action-color)] transition-colors duration-150 enabled:cursor-pointer enabled:hover:text-[var(--auth-footer-action-hover)] disabled:cursor-not-allowed'

export function AuthPanelPassword({
  variant = 'desktop',
  title = 'Вход',
  emailLabel = 'Email',
  emailPlaceholder = 'email@domain.com',
  emailValue,
  onEmailChange,
  passwordLabel = 'Пароль',
  passwordPlaceholder = 'Введите пароль',
  passwordValue,
  onPasswordChange,
  passwordHelperText,
  showAgreementCheckbox = false,
  agreementChecked,
  onAgreementChange,
  agreementErrorText,
  legalLinksReturnTo,
  submitLabel = 'Продолжить',
  onSubmit,
  submitDisabled = false,
  actionButtonsDisabled = false,
  onForgotPassword,
  onMagicLink,
  registrationHref = 'https://academy.productsense.io/register?next=/academy',
  footerMessageText,
  footerMessageTone = 'default',
  className,
}: AuthPanelPasswordProps) {
  const isMobile = variant === 'mobile'

  return (
    <AuthPanel
      variant={isMobile ? 'mobile' : 'desktop'}
      title={title}
      className={className}
      onSubmit={onSubmit}
      footer={
        <div
          className="flex w-full flex-col"
          style={{ gap: designTokens.spacing.authPanelFooterGap }}
        >
          <Button
            type="submit"
            variant={isMobile ? 'primaryMobile' : 'primary'}
            disabled={submitDisabled}
          >
            {submitLabel}
          </Button>
          <div className="flex w-full items-center justify-between">
            <button
              type="button"
              style={authFooterActionStyle}
              className={authFooterActionClassName}
              onClick={onForgotPassword}
              disabled={actionButtonsDisabled}
              aria-disabled={actionButtonsDisabled}
              data-testid="auth-forgot-password-button"
            >
              Забыли пароль?
            </button>
            <button
              type="button"
              style={authFooterActionStyle}
              className={authFooterActionClassName}
              onClick={onMagicLink}
              disabled={actionButtonsDisabled}
              aria-disabled={actionButtonsDisabled}
              data-testid="auth-magic-link-button"
            >
              Вход по ссылке
            </button>
          </div>
          {/* Регистрации здесь больше нет: воркфлоу, который заводил учётку,
              подменён заглушкой и всегда отвечает отказом. Новые люди заводятся
              в Академии, туда и отправляем — иначе человек будет перебирать
              пароли к аккаунту, которого не существует. */}
          <p
            style={{ ...authFooterTextStyle, color: designTokens.colors.text.secondary, margin: 0, textAlign: 'center' }}
            data-testid="auth-registration-hint"
          >
            Нет аккаунта?{' '}
            <a
              href={registrationHref}
              target="_blank"
              rel="noopener noreferrer"
              style={authFooterActionStyle}
              className={authFooterActionClassName}
              data-testid="auth-registration-link"
            >
              Зарегистрируйтесь в Академии
            </a>
          </p>
          {footerMessageText ? (
            <p
              style={{
                ...authFooterTextStyle,
                color:
                  footerMessageTone === 'error'
                    ? designTokens.colors.text.error
                    : footerMessageTone === 'success'
                      ? designTokens.colors.text.success
                      : designTokens.colors.text.secondary,
                margin: 0,
                textAlign: 'center',
              }}
            >
              {footerMessageText}
            </p>
          ) : null}
        </div>
      }
    >
      <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.lg }}>
        <InputField
          label={emailLabel}
          placeholder={emailPlaceholder}
          value={emailValue}
          onChange={(event) => onEmailChange?.(event.target.value)}
        />
        <PasswordInput
          label={passwordLabel}
          placeholder={passwordPlaceholder}
          value={passwordValue}
          onChange={(event) => onPasswordChange?.(event.target.value)}
          helperText={passwordHelperText}
        />
        {showAgreementCheckbox ? (
          <AuthAgreementCheckbox
            id="auth-password-agreement-checkbox"
            checked={agreementChecked}
            onCheckedChange={onAgreementChange}
            errorText={agreementErrorText}
            legalLinksReturnTo={legalLinksReturnTo}
          />
        ) : null}
      </div>
    </AuthPanel>
  )
}
